import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { MailOutline, ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../services/api';

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';
/** Must match backend `OTP_EXPIRY_MS` for password reset (10 minutes). */
const RESET_OTP_COOLDOWN_MS = 10 * 60 * 1000;

function formatCooldown(ms) {
  if (ms <= 0) return '0:00';
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email');
  const [error, setError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState(false);
  /** When `Date.now()` reaches this, show Resend (matches OTP validity window). */
  const [resendAllowedAfter, setResendAllowedAfter] = useState(null);
  const [tick, setTick] = useState(0);

  const normalizedEmail = String(email).trim().toLowerCase();

  useEffect(() => {
    if (step !== 'otp' || !resendAllowedAfter) return;
    if (Date.now() >= resendAllowedAfter) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [step, resendAllowedAfter]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: normalizedEmail });
      setResendAllowedAfter(Date.now() + RESET_OTP_COOLDOWN_MS);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setOtpError('');
    setResending(true);
    try {
      await api.post('/auth/resend-reset-otp', { email: normalizedEmail });
      setResendAllowedAfter(Date.now() + RESET_OTP_COOLDOWN_MS);
      setOtpCode('');
      setResetToken('');
    } catch {
      setOtpError('Could not resend the code. Try again in a moment.');
    }
    setResending(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    const digits = otpCode.replace(/\D/g, '');
    if (digits.length !== 6) {
      setOtpError('Enter the 6-digit code from your email.');
      return;
    }
    setOtpSubmitting(true);
    try {
      const { data } = await api.post('/auth/verify-reset-otp', {
        email: normalizedEmail,
        otp: digits,
      });
      setResetToken(data.resetToken);
      setStep('password');
    } catch (err) {
      const d = err.response?.data;
      setOtpError(d?.message || 'Could not verify the code. Try again or request a new code.');
    }
    setOtpSubmitting(false);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (!resetToken) {
      setPasswordError('Session expired. Verify your code again.');
      setStep('otp');
      return;
    }
    setPasswordSubmitting(true);
    try {
      await api.post('/auth/reset-password', {
        email: normalizedEmail,
        resetToken,
        password,
      });
      setSuccess(true);
    } catch (err) {
      const d = err.response?.data;
      setPasswordError(d?.message || 'Could not reset password. Verify your code again.');
      if (d?.expired) {
        setResetToken('');
        setStep('otp');
      }
    }
    setPasswordSubmitting(false);
  };

  const handleStartOver = () => {
    setStep('email');
    setEmail('');
    setOtpCode('');
    setResetToken('');
    setPassword('');
    setConfirmPassword('');
    setOtpError('');
    setPasswordError('');
    setResendAllowedAfter(null);
  };

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
          <MailOutline sx={{ fontSize: 64, color: PRIMARY, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 1 }}>
            Password updated
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
            You can now sign in with your new password.
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            sx={{
              py: 1.25,
              px: 4,
              bgcolor: PRIMARY,
              borderRadius: 1,
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
            }}
          >
            Go to Sign in
          </Button>
        </Box>
      </Box>
    );
  }

  if (step === 'password') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 420, width: '100%' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 1 }}>
            Choose a new password
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
            Code verified for <Box component="span" sx={{ fontWeight: 600, color: ACCENT }}>{normalizedEmail}</Box>.
            Enter your new password below.
          </Typography>
          <form onSubmit={handleSetPassword}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 1, textAlign: 'left' }}>
                {passwordError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="New password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm new password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              size="small"
              sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={passwordSubmitting}
              sx={{
                py: 1.25,
                mb: 2,
                bgcolor: PRIMARY,
                borderRadius: 1,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
              }}
            >
              {passwordSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Reset password'}
            </Button>
          </form>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Wrong email?{' '}
            <Link
              component="button"
              type="button"
              onClick={handleStartOver}
              sx={{ color: PRIMARY, textDecoration: 'none', font: 'inherit', cursor: 'pointer', background: 'none', border: 0, p: 0 }}
            >
              Start over
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  }

  if (step === 'otp') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 1 }}>
            Enter reset code
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6 }}>
            We sent a 6-digit code to
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: ACCENT, mb: 2 }}>
            {normalizedEmail}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
            Enter the code to continue. It expires in 10 minutes.
          </Typography>
          <form onSubmit={handleVerifyOtp}>
            {otpError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 1, textAlign: 'left' }}>
                {otpError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="6-digit code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 6,
                autoComplete: 'one-time-code',
                'aria-label': 'Password reset code',
              }}
              placeholder="000000"
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.2em',
                  fontSize: '1.25rem',
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={otpSubmitting || otpCode.replace(/\D/g, '').length !== 6}
              sx={{
                py: 1.25,
                mb: 2,
                bgcolor: PRIMARY,
                borderRadius: 1,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
              }}
            >
              {otpSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Verify code'}
            </Button>
          </form>
          <Box sx={{ bgcolor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 1, px: 2.5, py: 1.5, mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ color: '#9A3412', fontWeight: 600, fontSize: '12px', mb: 0.5 }}>
              Don&apos;t see the email?
            </Typography>
            <Typography variant="caption" sx={{ color: '#C2410C', lineHeight: 1.6, display: 'block' }}>
              Check your <b>Spam</b> or <b>Junk</b> folder.
            </Typography>
          </Box>
          {resendAllowedAfter && Date.now() < resendAllowedAfter ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              You can request a new code in{' '}
              <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: ACCENT }}>
                {formatCooldown(resendAllowedAfter - Date.now())}
              </Box>
            </Typography>
          ) : (
            <Button
              variant="outlined"
              onClick={handleResend}
              disabled={resending}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, px: 3, mb: 2 }}
            >
              {resending ? 'Sending…' : 'Resend code'}
            </Button>
          )}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Wrong email?{' '}
              <Link
                component="button"
                type="button"
                onClick={handleStartOver}
                sx={{ color: PRIMARY, textDecoration: 'none', font: 'inherit', cursor: 'pointer', background: 'none', border: 0, p: 0 }}
              >
                Start over
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Link
          component={RouterLink}
          to="/login"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: '13px',
            mb: 4,
            '&:hover': { color: PRIMARY },
          }}
        >
          <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} /> Back to Sign in
        </Link>

        <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 0.5 }}>
          Forgot your password?
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
          Enter the email for your account. We&apos;ll send a 6-digit code to reset your password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSendCode}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            size="small"
            sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.25,
              bgcolor: PRIMARY,
              borderRadius: 1,
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send reset code'}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
